const STOREFRONT_API_VERSION = '2025-01';

class FAQsWithFilters extends HTMLElement {
  constructor() {
    super();
  }

  static handleize(str) {
    return String(str ?? '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  connectedCallback() {
    this.filtersContainer = this.querySelector('.faqs-with-filters__filter-pills');
    this.bindShowMore();
    this.bindFilterPills();
    this.init();
  }

  disconnectedCallback() {
    if (this.showMoreButton && this._onShowMoreClick) {
      this.showMoreButton.removeEventListener('click', this._onShowMoreClick);
    }
    if (this.filtersContainer && this._onFilterPillClick) {
      this.filtersContainer.removeEventListener('click', this._onFilterPillClick);
    }
  }

  bindFilterPills() {
    if (!this.filtersContainer) return;
    this._onFilterPillClick = this.onFilterPillClick.bind(this);
    this.filtersContainer.addEventListener('click', this._onFilterPillClick);
  }

  static getItemTagHandles(item) {
    const tagsRaw = item.getAttribute('data-faq-tags') ?? '';
    return tagsRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((t) => FAQsWithFilters.handleize(t));
  }

  updateFaqItemVisibility() {
    const pills = this.filtersContainer?.querySelectorAll('.faqs-with-filters__pill.is-active') ?? [];
    const activeHandles = new Set([...pills].map((p) => FAQsWithFilters.handleize(p.textContent)));
    const showMoreExpanded = this.showMoreButton?.getAttribute('aria-expanded') === 'true';
    const filtersActive = activeHandles.size > 0;

    const questionsEl = this.querySelector('.faqs-with-filters__questions');
    if (questionsEl) {
      questionsEl.classList.toggle('faqs-with-filters__questions--filters-active', filtersActive);
    }

    this.querySelectorAll('.faqs-with-filters__item').forEach((item) => {
      const isExtra = item.classList.contains('faqs-with-filters__item--extra');
      const inBaseline = !isExtra || showMoreExpanded;
      const tagHandles = FAQsWithFilters.getItemTagHandles(item);
      const matchesAnyFilter = tagHandles.some((h) => activeHandles.has(h));

      const visible = filtersActive ? matchesAnyFilter : inBaseline;

      item.classList.toggle('is-active', visible);
      item.classList.toggle('faqs-with-filters__item--filter-out', filtersActive && !visible);
    });

    const showMoreWrap = this.querySelector('.faqs-with-filters__show-more');
    if (showMoreWrap) {
      const showMoreControlVisible = !showMoreExpanded && !filtersActive;
      showMoreWrap.classList.toggle('is-active', showMoreControlVisible);
      showMoreWrap.setAttribute('aria-hidden', showMoreControlVisible ? 'false' : 'true');
    }

    this.syncAllPillAriaPressed();
  }

  onFilterPillClick(event) {
    const pill = event.target.closest('.faqs-with-filters__pill');
    if (!pill || !this.filtersContainer.contains(pill)) return;

    pill.classList.toggle('is-active');
    this.updateFaqItemVisibility();
  }

  /** Keep toggle buttons in sync with `.is-active` (labels from Liquid ignore aria-pressed). */
  static syncPillAriaPressed(pill) {
    if (pill.tagName === 'BUTTON') {
      pill.setAttribute('aria-pressed', pill.classList.contains('is-active') ? 'true' : 'false');
    }
  }

  syncAllPillAriaPressed() {
    this.filtersContainer?.querySelectorAll('.faqs-with-filters__pill').forEach((p) => {
      FAQsWithFilters.syncPillAriaPressed(p);
    });
  }

  bindShowMore() {
    this.showMoreButton = this.querySelector('.js-faqs-show-more');
    if (!this.showMoreButton) return;
    this._onShowMoreClick = () => this.showMore();
    this.showMoreButton.addEventListener('click', this._onShowMoreClick);
  }

  /**
   * Reveals all FAQ rows that were hidden after the initial count and hides the control.
   */
  showMore() {
    if (this.showMoreButton) {
      this.showMoreButton.setAttribute('aria-expanded', 'true');
    }
    this.updateFaqItemVisibility();
  }

  // merge all available filters into an array without duplicates
  getAllFilters(source) {
    let faqItems = [];

    if (Array.isArray(source)) {
      faqItems = source.filter(Boolean);
    } else if (source && typeof source === 'object') {
      let metaobject = null;
      if ('metaobject' in source && source.metaobject) {
        metaobject = source.metaobject;
      } else if ('data' in source && source.data && typeof source.data === 'object' && 'metaobject' in source.data) {
        metaobject = source.data.metaobject;
      }
      const nodes = metaobject?.items?.references?.nodes;
      faqItems = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
    }

    if (faqItems.length === 0) {
      return [];
    }

    const seen = new Set();
    const out = [];

    for (const item of faqItems) {
      const raw = item?.filters?.value;
      if (raw == null || raw === '') {
        continue;
      }

      let parsed;
      try {
        parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        continue;
      }

      if (!Array.isArray(parsed)) {
        continue;
      }

      for (const label of parsed) {
        if (typeof label !== 'string') {
          continue;
        }
        const trimmed = label.trim();
        if (!trimmed || seen.has(trimmed)) {
          continue;
        }
        seen.add(trimmed);
        out.push(trimmed);
      }
    }

    return out;
  }

  hideFilterSkeletons() {
    this.filtersContainer?.querySelectorAll('.faqs-with-filters__filter-pill-skeleton').forEach((el) => {
      el.hidden = true;
    });
    this.filtersContainer?.classList.remove('loading');
  }

  renderFilterButtons(filters) {
    if (!this.filtersContainer) return;
    this.filtersContainer.innerHTML = '';
    for (const filter of filters) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'faqs-with-filters__pill';
      btn.dataset.filter = filter;
      btn.textContent = filter;
      btn.setAttribute('aria-pressed', 'false');
      this.filtersContainer.appendChild(btn);
    }
  }

  async init() {
    const handle = this.dataset.faqGroupHandle?.trim() ?? '';
    const sourceType = this.dataset.sourceType?.trim() ?? '';

    if (sourceType === 'blocks' || !handle) {
      this.hideFilterSkeletons();
      return;
    }

    const storefrontAccessToken = window.storefrontApiKey;

    if (!storefrontAccessToken) {
      console.warn('FAQsWithFilters: window.storefrontApiKey not set');
      this.hideFilterSkeletons();
      return;
    }

    const query = `
      query FaqGroupMetaobject($handle: String!) {
        metaobject(handle: { handle: $handle, type: "faq_group" }) {
          id
          handle
          type
          items: field(key: "items") {
            key
            type
            value
            references(first: 50) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  type
                  question: field(key: "question") {
                    key
                    type
                    value
                  }
                  answer: field(key: "answer") {
                    key
                    type
                    value
                  }
                  filters: field(key: "filters") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    `;

    const graphqlUrl = `/api/${STOREFRONT_API_VERSION}/graphql.json`;

    try {
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        },
        body: JSON.stringify({
          query,
          variables: { handle },
        }),
      });

      const bodyText = await response.text();

      if (!response.ok) {
        console.error('FAQsWithFilters: HTTP', response.status, response.statusText, bodyText);
        this.hideFilterSkeletons();
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(bodyText);
      } catch (parseErr) {
        console.error('FAQsWithFilters: response is not JSON', parseErr, bodyText);
        this.hideFilterSkeletons();
        return;
      }

      const { data, errors } = parsed;

      if (errors?.length) {
        console.error('FAQsWithFilters GraphQL errors:', errors);
        this.hideFilterSkeletons();
        return;
      }

      const metaobject = data?.metaobject ?? null;
      this.faqGroup = metaobject;
      this.faqItems = metaobject?.items?.references?.nodes?.filter(Boolean) ?? [];
      this.allFilters = this.getAllFilters(data);

      this.renderFilterButtons(this.allFilters);
      this.hideFilterSkeletons();
      this.syncAllPillAriaPressed();
    } catch (err) {
      console.error('FAQsWithFilters fetch error:', err);
      this.hideFilterSkeletons();
    }
  }
}

if (!customElements.get('faqs-with-filters')) {
  customElements.define('faqs-with-filters', FAQsWithFilters);
}
